/* eslint-disable react/prop-types */
import * as React from "react";
import { m, LazyMotion, domAnimation } from "framer-motion";
import ToastContainer from "./ToastContainer";
import { cx } from "utils/join-classnames";
import { IconNames } from "core/models";
import { Icon, Button, Spinner } from "ui/components";
import styles from "./Toast.module.scss";

type Props = {
    id: string;
    title: React.ReactNode;
    removeToast(toastId: string): void;
    duration?: number;
    description?: string;
    type: "normal" | "error" | "success";
    icon?: IconNames;
    theme?: "light" | "dark";
};
type ComponentType = React.FunctionComponent<Props> & {
    Container: typeof ToastContainer;
};

const Toast: ComponentType = ({
    id,
    title,
    description,
    removeToast,
    duration = 5000,
    type = "normal",
    theme = "light",
}) => {
    const [, setDurationId] = React.useState<number | null>(null);
    const timeoutIdRef = React.useRef<number | null>(null);
    const stopClearTimer = React.useCallback(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
        setDurationId(null);
    }, []);
    const startClearTimer = React.useCallback(() => {
        stopClearTimer();
        timeoutIdRef.current = window.setTimeout(() => {
            removeToast(id);
        }, duration);
        setDurationId(Math.random());
    }, [id, removeToast, duration]);
    const handleRemove = React.useCallback(() => {
        removeToast(id);
    }, [id, removeToast]);

    const handleMouseOver = () => {
        stopClearTimer();
    };
    const handleMouseLeave = () => {
        if (duration && duration > 0) {
            startClearTimer();
        }
    };

    React.useEffect(() => {
        if (duration && duration > 0) {
            startClearTimer();
        }
        return () => stopClearTimer();
    }, [startClearTimer, stopClearTimer]);

    return (
        <LazyMotion features={domAnimation} strict>
            <m.div
                onMouseOver={handleMouseOver}
                onFocus={handleMouseOver}
                onMouseLeave={handleMouseLeave}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                className={cx(
                    styles.toast,
                    type === "error" ? styles.toast__error : null,
                    theme === "dark" ? styles.toast__dark : null,
                )}
            >
                <div
                    className={cx(
                        styles.toast__icon,
                        type === "error" ? styles.toast__icon__error : null,
                        type === "success" ? styles.toast__icon__success : null,
                    )}
                >
                    {type === "normal" && <Spinner />}
                    {type === "success" && <Icon name="confirm" />}
                    {type === "error" && <Icon name="error" />}
                </div>
                <div className={styles.toast__content}>
                    <div className={styles.toast__content__title}>{title}</div>
                    <div className={styles.toast__content__description}>
                        {description}
                    </div>
                </div>
                <div className={styles.toast__close__button}>
                    <Button
                        type="function"
                        theme={
                            type === "error" || theme === "dark"
                                ? "light"
                                : "dark"
                        }
                        icon="close"
                        onClick={handleRemove}
                        onRed={type === "error"}
                    />
                </div>
            </m.div>
        </LazyMotion>
    );
};

Toast.Container = ToastContainer;

export default Toast;
